import type { FC } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type {
  BeneficialOwnershipSpecificMetadata,
  CustomField,
  CustomRadioField,
  CustomSelectField,
  IANATimezones,
  OptionPoints
} from '@gcv/shared';
import { CustomFieldType } from '@gcv/shared';
import { Box } from '@mui/material';

import { InputRadio, InputSelect, NoticeBar } from 'ui';
import { parseFieldOptions } from './option-points.util';
import { PointsBadge } from './points-badge';
import { getField } from './preview-form-util';

export interface ScoredFieldControlProps {
  field: CustomField;
  sectionId: string;
  form: UseFormReturn<FieldValues>;
  timezone: IANATimezones;
  onValueChange: () => void;
  interpolationValues?: BeneficialOwnershipSpecificMetadata;
}

const optionPointsOf = (field: CustomField): OptionPoints | undefined =>
  (field as CustomRadioField | CustomSelectField).option_points;

/**
 * One question on a scored form. Radio and select render here rather than through `getField`, which
 * has nowhere to put per-answer points; everything else falls through to it unchanged.
 */
export const ScoredFieldControl: FC<ScoredFieldControlProps> = (props) => {
  const { field, form, onValueChange } = props;
  const points = optionPointsOf(field);

  // getField's File uploader has no-op handlers — it would take a file and silently drop it.
  if (field.type === CustomFieldType.File) {
    return (
      <NoticeBar
        type="warning"
        title={field.label}
        subtitle="File uploads aren't supported on risk assessments yet. Please share this document with your administrator directly."
      />
    );
  }

  if (points && field.type === CustomFieldType.Radio) {
    return (
      // InputRadio's FormControl sizes to content; full width keeps every badge in one column.
      <Box sx={{ '& .MuiFormControl-root': { width: '100%' } }}>
        <InputRadio
          label={field.label}
          name={field.id}
          justifyContent="column"
          options={parseFieldOptions((field as CustomRadioField).options).map((option) => ({
            label: option,
            value: option,
            details: <PointsBadge points={points[option] ?? 0} selected={form.watch(field.id) === option} />
          }))}
          rules={{ required: { message: 'is required', value: field.required || false } }}
          onBlur={onValueChange}
          onChange={onValueChange}
          {...form}
        />
      </Box>
    );
  }

  if (points && field.type === CustomFieldType.Select) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <InputSelect
            label={field.label}
            name={field.id}
            listItemsWidth="auto"
            options={parseFieldOptions((field as CustomSelectField).options).map((option) => ({
              label: option,
              value: option
            }))}
            rules={{ required: { message: 'is required', value: field.required || false } }}
            additionalOnChange={onValueChange}
            {...form}
          />
        </Box>
        <PointsBadge points={points[form.watch(field.id)] ?? 0} selected />
      </Box>
    );
  }

  return (
    <>{getField(field, props.sectionId, form, onValueChange, props.timezone, props.interpolationValues)}</>
  );
};
